import { RegistrationInteractionTypeEnum } from "@data-types/analytics-tracker/RegistrationInteractionTypeEnum";
import { DefaultPath } from "@utils/common/defaultRoutePathsEnum";

import { analyticsTracker } from "./AnalyticsTracker";

const REGISTRATION_FIELD_NAMES = new Set<string>([
	"acceptProtectionOfPlayerMoney",
	"acceptTermsAndConditions",
	"accountCreateAcceptance",
	"birthName",
	"birthPlace",
	"city",
	"confirmPassword",
	"countryId",
	"cultureInfoName",
	"day",
	"dob",
	"email",
	"estimatedGamingAmountId",
	"firstName",
	"houseNumber",
	"languageId",
	"lastName",
	"limitAmount",
	"limitDurationId",
	"limitOption",
	"mobilePhone",
	"month",
	"nationality",
	"nationalityId",
	"occupationId",
	"password",
	"personalIdentificator",
	"playerPromoSendingMode",
	"regionId",
	"repeatEmail",
	"street",
	"titleId",
	"userTitleId",
	"username",
	"year",
	"zipCode",
]);

const FIELD_TAG_NAMES = new Set(["INPUT", "SELECT", "TEXTAREA"]);

const LOGO_SELECTOR = "a.card--onboarding__logo, a.header--root__figure__link";
const LOGIN_PATH_SEGMENT = "/auth/login";

class RegistrationInteractionTracker {
	private filledFields = new Set<string>();
	private isAttached = false;

	attach = () => {
		if (this.isAttached) {
			return;
		}

		this.filledFields.clear();

		document.addEventListener("click", this.onClick, true);
		document.addEventListener("focusout", this.onFocusOut, true);

		this.isAttached = true;
	};

	detach = () => {
		if (!this.isAttached) {
			return;
		}

		document.removeEventListener("click", this.onClick, true);
		document.removeEventListener("focusout", this.onFocusOut, true);

		this.filledFields.clear();

		this.isAttached = false;
	};

	private onClick = (event: Event) => {
		const anchor = (event.target as Element | null)?.closest?.("a");

		if (!anchor) {
			return;
		}

		const href = anchor.getAttribute("href");

		if (!href || href.startsWith("#") || href.startsWith("javascript:")) {
			return;
		}

		const label = this.classifyExit(anchor as HTMLAnchorElement);

		if (!label) {
			return;
		}

		analyticsTracker.trackRegistrationInteraction(label);
	};

	private onFocusOut = (event: Event) => {
		const field = event.target as HTMLInputElement | null;

		if (!field || !FIELD_TAG_NAMES.has(field.tagName)) {
			return;
		}

		if (!REGISTRATION_FIELD_NAMES.has(field.name)) {
			return;
		}

		if (!this.hasValue(field) || this.filledFields.has(field.name)) {
			return;
		}

		this.filledFields.add(field.name);

		analyticsTracker.trackRegistrationInteraction(
			RegistrationInteractionTypeEnum.FieldFilled,
			{ field_name: field.name }
		);
	};

	private classifyExit(anchor: HTMLAnchorElement) {
		if (this.isLogo(anchor)) {
			return RegistrationInteractionTypeEnum.ExitLogo;
		}

		if (anchor.pathname?.includes(LOGIN_PATH_SEGMENT)) {
			return RegistrationInteractionTypeEnum.ExitLogin;
		}

		if (anchor.origin && anchor.origin !== window.location.origin) {
			return null;
		}

		return RegistrationInteractionTypeEnum.ExitOther;
	}

	private isLogo(anchor: HTMLAnchorElement) {
		if (anchor.matches(LOGO_SELECTOR)) {
			return true;
		}

		return (
			!!anchor.pathname?.includes(DefaultPath.HOME) &&
			!!anchor.querySelector("img")
		);
	}

	private hasValue(field: HTMLInputElement) {
		if (field.type === "checkbox" || field.type === "radio") {
			return field.checked;
		}

		return Boolean(field.value?.trim());
	}
}

const registrationInteractionTracker = new RegistrationInteractionTracker();

export { registrationInteractionTracker };
